import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  Calculator, Plus, Loader2, Trash2, Edit, Search, Euro, Building2,
  ChevronDown, ChevronUp, TrendingUp, CheckCircle2, AlertCircle,
  Ruler, Calendar, BarChart3, Upload, Camera, FileSpreadsheet
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const ComputoMetrico = () => {
  const [cantieri, setCantieri] = useState([]);
  const [selectedCantiere, setSelectedCantiere] = useState('');
  const [sal, setSal] = useState(null);
  const [misure, setMisure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddVoce, setShowAddVoce] = useState(false);
  const [newVoce, setNewVoce] = useState({ codice: '', descrizione: '', unita: 'm²', quantita_prevista: '', prezzo_unitario: '' });
  const [expandedVoce, setExpandedVoce] = useState(null);
  const [showAddMisura, setShowAddMisura] = useState(null);
  const [newMisura, setNewMisura] = useState({ date: '', quantita: '', lunghezza: '', larghezza: '', altezza: '', note: '' });
  const [editingVoce, setEditingVoce] = useState(null);
  const [editVData, setEditVData] = useState({});
  const [importing, setImporting] = useState(false);
  const pageRef = useRef(null);

  const loadCantieri = async () => {
    try { setCantieri(await api.getCantieri()); } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadSAL = async (cId) => {
    if (!cId) { setSal(null); return; }
    try {
      const [s, m] = await Promise.all([api.getSAL(cId), api.getMisure(cId)]);
      setSal(s); setMisure(m);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadCantieri(); }, []);
  useEffect(() => { if (selectedCantiere) loadSAL(selectedCantiere); }, [selectedCantiere]);

  const handleAddVoce = async () => {
    if (!newVoce.descrizione || !selectedCantiere) return;
    try {
      await api.createVoceComputo({
        cantiereId: selectedCantiere, codice: newVoce.codice, descrizione: newVoce.descrizione,
        unita: newVoce.unita, quantita_prevista: parseFloat(newVoce.quantita_prevista) || 0,
        prezzo_unitario: parseFloat(newVoce.prezzo_unitario) || 0
      });
      setNewVoce({ codice: '', descrizione: '', unita: 'm²', quantita_prevista: '', prezzo_unitario: '' });
      setShowAddVoce(false); loadSAL(selectedCantiere);
    } catch (e) { console.error(e); }
  };

  const handleDeleteVoce = async (id) => {
    try { await api.deleteVoceComputo(id); loadSAL(selectedCantiere); } catch (e) { console.error(e); }
  };

  const handleAddMisura = async () => {
    if (!showAddMisura || (!newMisura.quantita && !newMisura.lunghezza)) return;
    try {
      await api.createMisura({
        voceId: showAddMisura, cantiereId: selectedCantiere,
        date: newMisura.date || new Date().toISOString().split('T')[0],
        quantita: parseFloat(newMisura.quantita) || 0,
        lunghezza: parseFloat(newMisura.lunghezza) || 0,
        larghezza: parseFloat(newMisura.larghezza) || 0,
        altezza: parseFloat(newMisura.altezza) || 0,
        note: newMisura.note
      });
      setNewMisura({ date: '', quantita: '', lunghezza: '', larghezza: '', altezza: '', note: '' });
      setShowAddMisura(null); loadSAL(selectedCantiere);
    } catch (e) { console.error(e); }
  };

  const handleDeleteMisura = async (id) => {
    try { await api.deleteMisura(id); loadSAL(selectedCantiere); } catch (e) { console.error(e); }
  };

  const openEditVoce = (v) => {
    setEditVData({ codice: v.codice, descrizione: v.descrizione, unita: v.unita, quantita_prevista: String(v.quantita_prevista), prezzo_unitario: String(v.prezzo_unitario) });
    setEditingVoce(v);
  };

  const handleSaveVoce = async () => {
    if (!editingVoce) return;
    try {
      await api.updateVoceComputo(editingVoce.id, {
        ...editVData, quantita_prevista: parseFloat(editVData.quantita_prevista) || 0,
        prezzo_unitario: parseFloat(editVData.prezzo_unitario) || 0
      });
      setEditingVoce(null); loadSAL(selectedCantiere);
    } catch (e) { console.error(e); }
  };

  const getMisureForVoce = (voceId) => misure.filter(m => m.voceId === voceId);

  const handleImportScan = async (file) => {
    if (!file || !selectedCantiere) return;
    setImporting(true);
    try {
      const result = await api.importComputoScan(selectedCantiere, file);
      if (result.success) {
        alert(`Importate ${result.voci_importate} voci dal computo!`);
        loadSAL(selectedCantiere);
      }
    } catch (e) { alert('Errore scansione: ' + (e.response?.data?.detail || e.message)); }
    setImporting(false);
  };

  const handleImportExcel = async (file) => {
    if (!file || !selectedCantiere) return;
    setImporting(true);
    try {
      const result = await api.importComputoExcel(selectedCantiere, file);
      if (result.success) {
        alert(`Importate ${result.voci_importate} voci dall'Excel!`);
        loadSAL(selectedCantiere);
      }
    } catch (e) { alert('Errore importazione: ' + (e.response?.data?.detail || e.message)); }
    setImporting(false);
  };

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  const chartData = sal?.voci?.map(v => ({
    name: v.descrizione.length > 20 ? v.descrizione.slice(0, 20) + '...' : v.descrizione,
    Previsto: v.totale_previsto || 0,
    Eseguito: v.totale_eseguito || 0,
  })) || [];

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Computo Metrico e SAL" subtitle={sal ? `Avanzamento: ${sal.percentuale}%` : ''} />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Computo Metrico e SAL</h1>
          <p className="page-subtitle">Preventivo, misure e stato avanzamento lavori</p>
        </div>
        <div className="header-right"><SectionActions sectionTitle="Computo Metrico" contentRef={pageRef} /></div>
      </div>

      {/* Cantiere Selector */}
      <div style={{ padding: '16px 28px' }}>
        <Label style={{ marginBottom: 6, display: 'block' }}>Seleziona Cantiere</Label>
        <Select value={selectedCantiere} onValueChange={setSelectedCantiere}>
          <SelectTrigger style={{ maxWidth: 400 }}><SelectValue placeholder="Scegli un cantiere..." /></SelectTrigger>
          <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.address}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {selectedCantiere && sal && (
        <>
          {/* SAL Overview */}
          <div className="sal-overview">
            <div className="sal-card sal-card-main">
              <div className="sal-progress-ring">
                <span className="sal-percent">{sal.percentuale}%</span>
              </div>
              <div>
                <h3>Stato Avanzamento Lavori</h3>
                <p>{sal.voci?.length || 0} voci nel computo</p>
              </div>
            </div>
            <div className="sal-card">
              <Euro size={20} color="#3b82f6" />
              <span className="sal-card-value">€{sal.totale_previsto.toFixed(2)}</span>
              <span className="sal-card-label">Preventivo</span>
            </div>
            <div className="sal-card">
              <TrendingUp size={20} color="#22c55e" />
              <span className="sal-card-value" style={{ color: '#22c55e' }}>€{sal.totale_eseguito.toFixed(2)}</span>
              <span className="sal-card-label">Eseguito</span>
            </div>
            <div className="sal-card">
              <AlertCircle size={20} color={sal.differenza >= 0 ? '#f59e0b' : '#ef4444'} />
              <span className="sal-card-value" style={{ color: sal.differenza >= 0 ? '#f59e0b' : '#ef4444' }}>€{sal.differenza.toFixed(2)}</span>
              <span className="sal-card-label">Rimanenza</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div style={{ padding: '0 28px 16px' }}>
            <Progress value={sal.percentuale} className="sal-bar" />
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div style={{ padding: '0 28px 20px' }}>
              <div className="chart-card">
                <h3 className="chart-title">Previsto vs Eseguito per Voce</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                    <Bar dataKey="Previsto" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Eseguito" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Import & Add Buttons */}
          <div className="mag-action-row">
            <button className="big-action-btn" style={{ flex: 1 }} onClick={() => setShowAddVoce(true)}>
              <Plus size={20} /> Aggiungi Voce Manuale
            </button>
            <label className="big-action-btn" style={{ flex: 1, borderColor: '#8b5cf6', color: '#8b5cf6', cursor: 'pointer' }}>
              {importing ? <><Loader2 size={20} className="spin" /> Scansione AI...</> : <><Camera size={20} /> Importa da PDF/Foto (AI)</>}
              <input type="file" accept="image/*,.pdf,.jpg,.jpeg,.png,.heic" capture="environment" style={{ display: 'none' }} disabled={importing} onChange={e => { if (e.target.files[0]) handleImportScan(e.target.files[0]); e.target.value = ''; }} />
            </label>
            <label className="big-action-btn" style={{ flex: 1, borderColor: '#22c55e', color: '#22c55e', cursor: 'pointer' }}>
              <FileSpreadsheet size={20} /> Importa da Excel
              <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleImportExcel(e.target.files[0]); e.target.value = ''; }} />
            </label>
          </div>

          {/* Voci List */}
          <div className="computo-list">
            {(sal.voci || []).map((v, i) => {
              const perc = v.quantita_prevista > 0 ? Math.min(100, (v.quantita_eseguita / v.quantita_prevista) * 100) : 0;
              const voceMisure = getMisureForVoce(v.id);
              const isExpanded = expandedVoce === v.id;
              return (
                <div key={v.id} className={`computo-card ${isExpanded ? 'computo-expanded' : ''}`}>
                  <div className="computo-row" onClick={() => setExpandedVoce(isExpanded ? null : v.id)}>
                    <div className="computo-num">{v.codice || (i + 1)}</div>
                    <div className="computo-info">
                      <h4>{v.descrizione}</h4>
                      <p>{v.quantita_prevista} {v.unita} × €{v.prezzo_unitario.toFixed(2)} = <strong>€{v.totale_previsto.toFixed(2)}</strong></p>
                    </div>
                    <div className="computo-progress">
                      <div className="computo-prog-bar"><div className="computo-prog-fill" style={{ width: `${perc}%`, background: perc >= 100 ? '#22c55e' : '#3b82f6' }} /></div>
                      <span className="computo-perc">{perc.toFixed(0)}%</span>
                    </div>
                    <div className="computo-amounts">
                      <span className="computo-exec">€{v.totale_eseguito.toFixed(2)}</span>
                      <span className="computo-remain">rest. €{(v.totale_previsto - v.totale_eseguito).toFixed(2)}</span>
                    </div>
                    <div className="computo-actions">
                      <button className="mag-action-btn-sm" onClick={(e) => { e.stopPropagation(); setShowAddMisura(v.id); setNewMisura({ date: new Date().toISOString().split('T')[0], quantita: '', lunghezza: '', larghezza: '', altezza: '', note: '' }); }} title="Registra Misura"><Ruler size={13} /></button>
                      <button className="mag-action-btn-sm" onClick={(e) => { e.stopPropagation(); openEditVoce(v); }}><Edit size={13} /></button>
                      <button className="mag-action-btn-sm" style={{ color: '#d1d5db' }} onClick={(e) => { e.stopPropagation(); handleDeleteVoce(v.id); }}><Trash2 size={13} /></button>
                    </div>
                    {isExpanded ? <ChevronUp size={16} color="#9ca3af" /> : <ChevronDown size={16} color="#9ca3af" />}
                  </div>

                  {isExpanded && (
                    <div className="computo-detail">
                      <div className="computo-detail-header">
                        <h5><Ruler size={14} /> Libretto Misure ({voceMisure.length} registrazioni)</h5>
                        <button className="dcb-add-btn" onClick={() => { setShowAddMisura(v.id); setNewMisura({ date: new Date().toISOString().split('T')[0], quantita: '', lunghezza: '', larghezza: '', altezza: '', note: '' }); }}>
                          <Plus size={13} /> Nuova Misura
                        </button>
                      </div>
                      <div className="computo-misure-list">
                        {voceMisure.map(m => (
                          <div key={m.id} className="computo-misura-row">
                            <Calendar size={13} color="#6b7280" />
                            <span className="cm-date">{new Date(m.date + 'T12:00').toLocaleDateString('it-IT')}</span>
                            <span className="cm-qty"><strong>{m.quantita?.toFixed(2)}</strong> {v.unita}</span>
                            {m.lunghezza > 0 && <span className="cm-dim">{m.lunghezza}×{m.larghezza || 1}{m.altezza > 0 ? `×${m.altezza}` : ''}</span>}
                            {m.note && <span className="cm-note">{m.note}</span>}
                            <button className="pa-delete" onClick={() => handleDeleteMisura(m.id)}><Trash2 size={12} /></button>
                          </div>
                        ))}
                        {voceMisure.length === 0 && <p className="empty-text">Nessuna misura. Registra la prima!</p>}
                      </div>
                      <div className="computo-detail-summary">
                        Totale eseguito: <strong>{v.quantita_eseguita?.toFixed(2)} {v.unita}</strong> di {v.quantita_prevista} previsti — <strong>€{v.totale_eseguito.toFixed(2)}</strong> di €{v.totale_previsto.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {(!sal.voci || sal.voci.length === 0) && <p className="empty-text">Nessuna voce nel computo. Aggiungi la prima!</p>}
          </div>
        </>
      )}

      {!selectedCantiere && <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}><Calculator size={48} /><h3 style={{ margin: '12px 0 4px', color: '#6b7280' }}>Seleziona un Cantiere</h3><p>Per visualizzare il computo metrico e lo stato avanzamento lavori</p></div>}

      {/* Add Voce Dialog */}
      {showAddVoce && (
        <Dialog open={showAddVoce} onOpenChange={setShowAddVoce}><DialogContent>
          <DialogHeader><DialogTitle>Nuova Voce Computo</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-row">
              <div className="form-field"><Label>Codice</Label><Input value={newVoce.codice} onChange={e => setNewVoce({...newVoce, codice: e.target.value})} placeholder="A.01" /></div>
              <div className="form-field"><Label>Unità di Misura</Label><Input value={newVoce.unita} onChange={e => setNewVoce({...newVoce, unita: e.target.value})} placeholder="m², ml, m³, kg" /></div>
            </div>
            <div className="form-field"><Label>Descrizione Lavorazione *</Label><Input value={newVoce.descrizione} onChange={e => setNewVoce({...newVoce, descrizione: e.target.value})} placeholder="Es: Scavo fondazioni" /></div>
            <div className="form-row">
              <div className="form-field"><Label>Quantità Prevista</Label><Input type="number" step="0.01" value={newVoce.quantita_prevista} onChange={e => setNewVoce({...newVoce, quantita_prevista: e.target.value})} /></div>
              <div className="form-field"><Label>Prezzo Unitario (€)</Label><Input type="number" step="0.01" value={newVoce.prezzo_unitario} onChange={e => setNewVoce({...newVoce, prezzo_unitario: e.target.value})} /></div>
            </div>
            {newVoce.quantita_prevista && newVoce.prezzo_unitario && (
              <p style={{ fontSize: 14, fontWeight: 700, color: '#22c55e', textAlign: 'right' }}>
                Totale: €{((parseFloat(newVoce.quantita_prevista) || 0) * (parseFloat(newVoce.prezzo_unitario) || 0)).toFixed(2)}
              </p>
            )}
            <button className="form-submit" onClick={handleAddVoce} disabled={!newVoce.descrizione}>Aggiungi al Computo</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Edit Voce Dialog */}
      {editingVoce && (
        <Dialog open={!!editingVoce} onOpenChange={() => setEditingVoce(null)}><DialogContent>
          <DialogHeader><DialogTitle>Modifica Voce</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-row">
              <div className="form-field"><Label>Codice</Label><Input value={editVData.codice || ''} onChange={e => setEditVData({...editVData, codice: e.target.value})} /></div>
              <div className="form-field"><Label>Unità</Label><Input value={editVData.unita || ''} onChange={e => setEditVData({...editVData, unita: e.target.value})} /></div>
            </div>
            <div className="form-field"><Label>Descrizione</Label><Input value={editVData.descrizione || ''} onChange={e => setEditVData({...editVData, descrizione: e.target.value})} /></div>
            <div className="form-row">
              <div className="form-field"><Label>Quantità Prevista</Label><Input type="number" step="0.01" value={editVData.quantita_prevista || ''} onChange={e => setEditVData({...editVData, quantita_prevista: e.target.value})} /></div>
              <div className="form-field"><Label>Prezzo Unitario (€)</Label><Input type="number" step="0.01" value={editVData.prezzo_unitario || ''} onChange={e => setEditVData({...editVData, prezzo_unitario: e.target.value})} /></div>
            </div>
            <button className="form-submit" onClick={handleSaveVoce}>Salva</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Add Misura Dialog */}
      {showAddMisura && (
        <Dialog open={!!showAddMisura} onOpenChange={() => setShowAddMisura(null)}><DialogContent>
          <DialogHeader><DialogTitle>Registra Misura</DialogTitle></DialogHeader>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>Inserisci la quantità direttamente oppure le dimensioni (L×l×h) per calcolarla automaticamente.</p>
          <div className="form-fields">
            <div className="form-field"><Label>Data</Label><Input type="date" value={newMisura.date} onChange={e => setNewMisura({...newMisura, date: e.target.value})} /></div>
            <div className="form-field"><Label>Quantità (diretta)</Label><Input type="number" step="0.01" value={newMisura.quantita} onChange={e => setNewMisura({...newMisura, quantita: e.target.value})} placeholder="Oppure usa le dimensioni sotto" /></div>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '0', textAlign: 'center' }}>— oppure calcola da dimensioni —</p>
            <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div className="form-field"><Label>Lunghezza</Label><Input type="number" step="0.01" value={newMisura.lunghezza} onChange={e => setNewMisura({...newMisura, lunghezza: e.target.value})} /></div>
              <div className="form-field"><Label>Larghezza</Label><Input type="number" step="0.01" value={newMisura.larghezza} onChange={e => setNewMisura({...newMisura, larghezza: e.target.value})} /></div>
              <div className="form-field"><Label>Altezza</Label><Input type="number" step="0.01" value={newMisura.altezza} onChange={e => setNewMisura({...newMisura, altezza: e.target.value})} /></div>
            </div>
            {newMisura.lunghezza && <p style={{ fontSize: 13, color: '#3b82f6', textAlign: 'right' }}>
              Calcolato: {((parseFloat(newMisura.lunghezza) || 0) * (parseFloat(newMisura.larghezza) || 1) * (parseFloat(newMisura.altezza) || 1)).toFixed(2)}
            </p>}
            <div className="form-field"><Label>Note</Label><Input value={newMisura.note} onChange={e => setNewMisura({...newMisura, note: e.target.value})} placeholder="Es: Muro est piano terra" /></div>
            <button className="form-submit" onClick={handleAddMisura}>Registra</button>
          </div>
        </DialogContent></Dialog>
      )}
    </div>
  );
};

export default ComputoMetrico;
